import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import CourseCard from "@/components/CourseCard";

interface CourseGridProps {
  isLoading: boolean;
  error: any;
  courses: any[];
  currentCourses: any[];
  currentPage: number;
  totalPages: number;
  pageNumbers: number[];
  handlePageChange: (page: number) => void;
  handleCourseClick: (courseId: string, slug?: string) => void;
}

const CoursesGrid: React.FC<CourseGridProps> = ({
  isLoading,
  error,
  courses,
  currentCourses,
  currentPage,
  totalPages,
  pageNumbers,
  handlePageChange,
  handleCourseClick,
}) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-[400px] rounded-lg" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8 bg-red-50 rounded-lg">
        <p className="text-red-500">Kursuste laadimine ebaõnnestus. Palun proovi hiljem uuesti.</p>
      </div>
    );
  }

  if (courses.length === 0) {
    return (
      <div className="text-center p-8 bg-yellow-50 rounded-lg">
        <p className="text-yellow-600">Kursused pole hetkel saadaval. Palun vaata hiljem uuesti.</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {currentCourses.map((course) => (
          <div
            key={course.id}
            onClick={() => handleCourseClick(course.id, course.slug)}
            className="cursor-pointer"
          >
            <CourseCard 
              course={{
                id: course.id,
                slug: course.slug,
                title: course.title,
                description: course.description,
                shortDescription: course.short_description,
                imageSrc: course.image_url,
                price: course.price,
                currency: course.currency || "EUR",
                duration: course.duration
              }} 
            />
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <Pagination className="mt-12">
          <PaginationContent>
            {currentPage > 1 && (
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => handlePageChange(currentPage - 1)}
                  className="cursor-pointer"
                />
              </PaginationItem>
            )}
            {pageNumbers.map(number => (
              <PaginationItem key={number}>
                <PaginationLink
                  isActive={currentPage === number}
                  onClick={() => handlePageChange(number)}
                  className="cursor-pointer"
                >
                  {number}
                </PaginationLink>
              </PaginationItem>
            ))}
            {currentPage < totalPages && (
              <PaginationItem>
                <PaginationNext 
                  onClick={() => handlePageChange(currentPage + 1)}
                  className="cursor-pointer"
                />
              </PaginationItem>
            )}
          </PaginationContent>
        </Pagination>
      )}
    </>
  );
};

export default CoursesGrid;
